import { api } from "./client";
import type {
  Company,
  CompanyDetail,
  Invitation,
  ListResult,
  Member,
  Organization,
  Overview,
  Product,
  ProfileCard,
  PushChanges,
  PushResult,
  Sample,
  SessionResponse,
  Task,
} from "@/types";
import { normalizeForSearch } from "@/lib/search-text";

/**
 * Every call the panel makes, in one thin typed layer.
 *
 * Pages use react-query against these rather than calling axios themselves, so
 * a change to a route or its shape happens in one file.
 */

export type ListParams = { query?: string; page?: number; limit?: number };

// --- Auth -----------------------------------------------------------------

/**
 * Starts a password reset.
 *
 * The server answers 204 whether or not the address is registered, so there is
 * nothing here to branch on — and nothing the panel could truthfully tell the
 * user beyond "check your inbox".
 */
export const requestPasswordReset = (email: string) =>
  api.post<void>("/auth/password-reset", { email }).then(() => undefined);

export const confirmPasswordReset = (token: string, password: string) =>
  api.post<void>("/auth/password-reset/confirm", { token, password }).then(() => undefined);

export const login = (email: string, password: string) =>
  api.post<SessionResponse>("/auth/login", { email, password }).then((r) => r.data);

export const me = () =>
  api.get<Omit<SessionResponse, "accessToken" | "refreshToken">>("/auth/me").then((r) => r.data);

export const logout = (refreshToken: string) =>
  api.post("/auth/logout", { refreshToken }).then(() => undefined);

// --- Organisation ---------------------------------------------------------

export const listOrganizations = () =>
  api.get<{ organizations: Organization[] }>("/organizations").then((r) => r.data.organizations);

export const createOrganization = (name: string) =>
  api.post<{ organization: { id: string; name: string } }>("/organizations", { name })
    .then((r) => r.data.organization);

export const listMembers = () =>
  api.get<{ members: Member[] }>("/organizations/members").then((r) => r.data.members);

export const listInvitations = () =>
  api.get<{ invitations: Invitation[] }>("/organizations/invitations")
    .then((r) => r.data.invitations);

export const invite = (email: string, role: "admin" | "viewer") =>
  api.post<{ invitation: Invitation & { token: string } }>("/organizations/invitations", {
    email,
    role,
  }).then((r) => r.data.invitation);

export const revokeInvitation = (id: string) =>
  api.delete(`/organizations/invitations/${id}`).then(() => undefined);

export const setMemberRole = (userId: string, role: "admin" | "viewer") =>
  api.patch<{ userId: string; role: string }>(`/organizations/members/${userId}`, { role })
    .then((r) => r.data);

export const removeMember = (userId: string) =>
  api.delete(`/organizations/members/${userId}`).then(() => undefined);

// --- Directory ------------------------------------------------------------

const listParams = (params: ListParams) => ({
  params: {
    // Folded before it is sent. These endpoints match against `searchText`,
    // which is stored folded — so a raw "\u015eirket" never matched the stored
    // "sirket", and every search containing a Turkish letter came back empty.
    ...(params.query ? { query: normalizeForSearch(params.query) } : {}),
    page: params.page ?? 0,
    limit: params.limit ?? 25,
  },
});

export const getOverview = () => api.get<Overview>("/overview").then((r) => r.data);

export const getCompanies = (params: ListParams) =>
  api.get<ListResult<Company>>("/companies", listParams(params)).then((r) => r.data);

export const getCompany = (id: string) =>
  api.get<CompanyDetail>(`/companies/${id}`).then((r) => r.data);

export const getProducts = (params: ListParams) =>
  api.get<ListResult<Product>>("/products", listParams(params)).then((r) => r.data);

export const getSamples = (params: ListParams) =>
  api.get<ListResult<Sample>>("/samples", listParams(params)).then((r) => r.data);

export const getTasks = (params: ListParams) =>
  api.get<ListResult<Task>>("/tasks", listParams(params)).then((r) => r.data);

/**
 * The panel's only write path for organisation data.
 *
 * Deliberately the same endpoint the phones use; `lib/sync.ts` explains why
 * and holds the rules a caller has to follow.
 */
export const pushSync = (changes: PushChanges) =>
  api.post<PushResult>("/sync/push", { changes }).then((r) => r.data);

// --- Profile card ---------------------------------------------------------
//
// The one part of the panel that is not about an organisation. A card belongs
// to the signed-in account and travels with it, so these calls are not scoped
// to `X-Org-Id` on the server — and the queries against them must not be keyed
// on the active organisation either, or switching organisations would throw
// away a card that never changed.

export const getMyCard = () =>
  api.get<{ card: ProfileCard | null }>("/auth/me/card").then((r) => r.data.card);

export type ProfileCardInput = {
  fullName: string;
  /** Blank is a valid answer: the server then stores the person's own name. */
  companyName: string;
  title: string;
  phone: string;
  email: string;
  website: string;
  city: string;
  country: string;
  photoKey?: string;
};

export const saveMyCard = (input: ProfileCardInput) =>
  api.put<{ card: ProfileCard }>("/auth/me/card", input).then((r) => r.data.card);

export const deleteMyCard = () => api.delete("/auth/me/card").then(() => undefined);

export const cardPhotoUploadUrl = (contentType: string) =>
  api
    .post<{ key: string; url: string }>("/auth/me/card/photo-url", { contentType })
    .then((r) => r.data);

/**
 * Sends the photo straight to storage.
 *
 * Deliberately not through [api]: the signature in the URL is the credential,
 * and S3 rejects a request that also carries an Authorization header. The
 * panel's base URL and interceptors have no business here either.
 */
export const uploadCardPhoto = async (url: string, file: File) => {
  const response = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
};
